import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { useAuthStore } from 'src/store/auth';
import { useGeneralStore } from 'src/store';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { cn } from '@utils/index';

import { HeaderMenu } from './HeaderMenu';

export const Header = () => {
  const { selectedStep, formSteps, handleJumpTo } = useFormControls();
  const { totalPlanPrice } = usePlan();
  const { siteConfig } = useGeneralStore();
  const { user } = useAuthStore();
  const { data, errorCount } = useOnboardingStore();

  const primaryApplicant = data.applicants?.[0];
  const displayName =
    primaryApplicant?.firstName || user?.firstName || user?.username || 'user';

  const location = useLocation();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const hideUserInfo = useMemo(() => {
    const hiddenPaths = [
      '/login',
      '/registration',
      '/forgot-password',
      '/reset-password',
    ];
    return hiddenPaths.includes(location.pathname);
  }, [location.pathname]);

  const onLogoClick = () => {
    const welcomeStepExists = formSteps.some(
      (step) => step.sectionName === 'Welcome'
    );
    const currentStep = formSteps[selectedStep];
    const isFinishUp = currentStep?.sectionName === 'Finish Up';

    if (
      isFinishUp ||
      location.pathname === '/thanks' ||
      location.pathname === '/healthhub'
    ) {
      window.location.href = window.location.origin + '/';
      return;
    }

    if (!welcomeStepExists) {
      window.location.href = '/registration';
      return;
    }

    handleJumpTo('Welcome');
  };

  const isHealthhub = location.pathname === '/healthhub';

  return (
    <div
      className={cn(
        "w-full z-40 left-0 flex flex-col gap-y-2 relative",
        !hideUserInfo && "max-xl:fixed max-xl:bg-background max-xl:top-0 max-xl:py-4"
      )}
      style={{ zIndex: 9997 }}
    >
      <div className="max-w-[1920px] relative mx-auto px-6 w-full max-lg:px-4.5">
        <div className="h-10 flex relative justify-between z-10 items-center rounded-full px-8 max-lg:px-3 max-lg:h-13.5 max-xl:h-18">
          {!!errorCount && (
            <div id="Oops" className="absolute flex bg-error font-extrabold justify-between items-center px-6 gap-3 h-full w-full left-0 z-20 text-white rounded-xl">
              <div className="flex items-center gap-3 2.5xl:w-[55%]">
                <img src="/icons/error-big.svg" alt="error icon" className="w-[30px] lg:w-[45px] h-auto" />
                <span className="text-sm md:text-base lg:text-lg">{errorCount > 1 ? 'Oops! We spotted some errors. Update the highlighted fields and try again.' : 'Oops! We spotted an error. Update the highlighted field and try again.'}</span>
              </div>
              {/* <span className="w-[45%] hidden 2.5xl:flex text-lg text-nowrap">{errorCount > 1 ? 'Oops! We spotted some errors. Update the highlighted fields and try again.' : 'Oops! We spotted an error. Update the highlighted field and try again.'}</span> */}
            </div>
          )}
          <div
            onClick={onLogoClick}
            className="flex flex-1 gap-2 gap-y-4 items-center max-w-36 cursor-pointer select-none"
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onLogoClick();
            }}
          >
            <img
              src={isHealthhub ? siteConfig?.images?.emailHeader : siteConfig?.logo}
              className="w-36 h-auto max-w-38 max-lg:max-w-23"
              alt="NGA"
            />
          </div>
          {!hideUserInfo && (
            <div className="flex items-center gap-4 xl:gap-40">
              {selectedStep > 1 && totalPlanPrice > 0 && location.pathname !== '/healthhub' && (
                <p className="hidden sm:flex items-center gap-2 xl:text-2xl text-base">
                  Total cost per period:
                  <span className="font-bold">
                    ${Number(totalPlanPrice).toFixed(2)}
                  </span>
                </p>
              )}
              <div className="flex items-center gap-13 max-lg:gap-1">
                {/* <p className="flex gap-1 text-[14px] -mr-5 max-lg:hidden !font-secondary text-[#002657]">
                  Hello
                  <strong className="!font-secondary">{displayName}</strong>
                </p> */}
                <div
                  className="relative flex items-center gap-13"
                  ref={dropdownRef}
                >
                  {/* <strong
                    id="user-name"
                    className="flex rounded-full bg-primary text-white text-sm justify-center items-center w-9 h-9 max-lg:max-w-6.5 max-lg:max-h-6.5 max-lg:text-[10px] lg:pointer-events-none pointer-events-auto"
                    onClick={() => setDropdownOpen((v) => !v)}
                  >
                    {displayName?.slice(0, 2).toLocaleUpperCase() || 'JS'}
                  </strong> */}
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((v) => !v)}
                    className="focus:outline-none font-semibold max-lg:hidden !font-secondary"
                  >
                    Hi,{' '}
                    <strong>{displayName}</strong>
                    <svg className="triangle-profile" width="15" height="15" viewBox="0 0 15 15">
                      <path d="M7.5 0 L0 12.5 Q0 15 2.5 15 L12.5 15 Q15 15 15 12.5 Z" fill="var(--color-primary)" transform="rotate(-90 7.5 7.5)" />
                    </svg>
                  </button>
                  {dropdownOpen && (
                    <HeaderMenu />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
